import React from "react";
import { FormattedMessage } from "react-intl";
import { object, array, number, string, boolean } from "yup";
import { Form, Button, Row, Space } from "@masterteam/mt-design-react";
import AdminFormModal from "../AdminFormModal";
import { ReactComponent as UpdateValueIcon } from "../../assets/images/update-value.svg";
import api from "../../providers/api";
import { addToast } from "../../helpers";
import UpdateForm from "./UpdateForm";

import styles from "./UpdateKpiModal.module.scss";

export const validationSchema = (isSemiAuto, hasNoBaseline) => {
  const shape = {
    Value: hasNoBaseline
      ? number().when(["IsRequiredValue", "IsSkipped"], {
          is: (isRequiredValue, isSkipped) => isRequiredValue && !isSkipped,
          then: number().required(),
          otherwise: number().nullable(),
        })
      : number()
          .nullable()
          .when(["IsSkipped"], {
            is: (isSkipped) => !isSkipped,
            then: number().required(),
          }),
    IsSkipped: boolean(),
    Justification: string()
      .nullable()
      .when(["IsMandatory", "IsSkipped"], {
        is: (isMandatory, isSkipped) => isMandatory && !isSkipped,
        then: (schema) => schema.required(" "),
      }),
    CorrectiveAction: string()
      .nullable()
      .when(["IsMandatory", "IsSkipped"], {
        is: (isMandatory, isSkipped) => isMandatory && !isSkipped,
        then: (schema) => schema.required(" "),
      }),
    MoreInformation: hasNoBaseline
      ? string().when(["IsRequiredValue", "IsSkipped"], {
          is: (isRequiredValue, isSkipped) => isRequiredValue && !isSkipped,
          then: string().required(),
          otherwise: string().nullable(),
        })
      : string()
          .nullable()
          .when(["IsSkipped"], {
            is: (isSkipped) => !isSkipped,
            then: string().required(),
          }),
    Attachments: hasNoBaseline
      ? array()
          .of(object())
          .when(["IsRequiredValue", "IsSkipped"], {
            is: (isRequiredValue, isSkipped) => isRequiredValue && !isSkipped,
            then: array().of(object()).min(1, " "),
            otherwise: array().of(object()),
          })
      : array()
          .of(object())
          .when(["IsSkipped"], {
            is: (isSkipped) => !isSkipped,
            then: array().of(object()).min(1, " "),
            otherwise: array().of(object()),
          }),
  };

  if (isSemiAuto) {
    shape.Parameters = array().of(
      object().shape({
        Value: number().required(),
      })
    );
  }

  return object().shape({
    measures: array().of(object().shape(shape)),
  });
};

function UpdateKpiModal({ kpi, closeModal, callback = () => {} }) {
  const isSemiAuto = kpi.DataSource?.toLowerCase() === "semi-auto";
  const hasNoBaseline = kpi?.HasNoBaseline;

  const isPercent = kpi.UnitOfMeasure === "Percentage";

  const initialValues = {
    measures: kpi.KPIMeasures?.filter((item) => item.AllowUpdate)?.map(
      (item) => ({
        measure: item,
        Value: "",
        IsSkipped: false,
        Justification: "",
        CorrectiveAction: "",
        MoreInformation: "",
        Attachments: [],
        Parameters: isSemiAuto
          ? kpi.Parameters.map((e) => {
              return { ...e, Value: null };
            })
          : undefined,
        MeasureID: item.ID,
        IsRequiredValue: item?.IsRequiredValue ?? false,
        IsMandatory: false,
      })
    ),
  };

  const getAttachment = async (item) => {
    const { Data: attachments } = await api.attachment.create(item.Attachments);
    if (!attachments) throw new Error("upload failed!");

    return {
      ...item,
      Attachments: attachments.map((e) => {
        return { ID: e };
      }),
    };
  };

  const handleSubmit = async (values, { setIsSubmitting }) => {
    let withAttachment = [];
    const withoutAttachment = [];
    values.measures.forEach(
      ({
        MeasureID,
        Value,
        IsSkipped,
        Attachments,
        Parameters,
        Justification,
        CorrectiveAction,
        MoreInformation,
      }) => {
        const item = {
          RelatedID: MeasureID,
          Value,
          IsSkipped,
          Parameters: isSemiAuto ? Parameters : undefined,
          Attachments,
          Justification,
          CorrectiveAction,
          MoreInformation,
        };
        if (item.Attachments?.length) {
          withAttachment.push(item);
        } else {
          withoutAttachment.push(item);
        }
      }
    );
    try {
      let newListWithAttachment = withAttachment;
      if (withAttachment?.length) {
        newListWithAttachment = await Promise.all(
          withAttachment.map(getAttachment)
        );
      }
      const body = [...newListWithAttachment, ...withoutAttachment];

      const { Data: submitRes } = await api.workflow.submit(body);
      if (!submitRes) throw new Error("save faild!");
      addToast.success("toast.successUpdate");
      callback();
      closeModal();
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "update-error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // function validate(values) {
  //   const result = values?.measures?.reduce((acc, curr) => {
  //     const res = +curr?.Value < curr?.measure?.Target;

  //     if (res)
  //       return (
  //         acc &&
  //         curr?.Justification?.trim()?.length &&
  //         curr?.CorrectiveAction?.trim()?.length
  //       );

  //     return acc;
  //   }, true);

  //   return !result;
  // }

  return (
    <AdminFormModal
      title={<FormattedMessage id="kpi-info-update-value" />}
      Icon={UpdateValueIcon}
      onClose={closeModal}
    >
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema(isSemiAuto, hasNoBaseline)}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, isValid, isSubmitting }) => (
          <>
            <Space direction="vertical" size="large">
              <Space
                direction="vertical"
                size="large"
                className={styles.formContent}
              >
                {values?.measures?.map((item, index) => (
                  <UpdateForm
                    key={item.MeasureID}
                    item={item}
                    index={index}
                    error={errors?.measures?.[index]}
                    isSemiAuto={isSemiAuto}
                    isPercent={isPercent}
                    kpi={kpi}
                    setFieldValue={setFieldValue}
                    values={values}
                    hasNoBaseline={hasNoBaseline}
                  />
                ))}
              </Space>
              <Row justify="end" size="small">
                <Button type="button" variant="dark" onClick={closeModal} fixed>
                  <FormattedMessage
                    id="kpi-info-cancel"
                    defaultMessage="kpi-info-cancel"
                  />
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid}
                  loading={isSubmitting}
                  fixed
                >
                  <FormattedMessage
                    id="kpi-info-save"
                    defaultMessage="kpi-info-save"
                  />
                </Button>
              </Row>
            </Space>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
}

export default UpdateKpiModal;
